import escape from 'lodash/escape';
import isEqual from 'lodash/isEqual';
import values from 'lodash/values';
import { makeObservable, override } from 'mobx';

import type { EntryDrugParams } from 'chaika-frontend-types/frontend-cms';
import { DictionaryNames, Locale } from 'chaika-enums';

import { EntryDrug, EntryDrugList } from '@store/models/SystemFields/EntryDrug';
import { AbstractListField, ListValueField } from './AbstractListField';
import { DateFormat, DateHelper } from '@utils/DateHelper';
import { store } from '@store';
import { isDefined } from '@utils/isDefined';

export class DrugField extends AbstractListField<EntryDrugList, EntryDrugParams> {
    public static isCreateParamsValid({ id, ...params }: EntryDrugParams): boolean {
        return values(params).filter(value => value && value !== '').length > 0;
    }

    public static getDrugPreview(drug: EntryDrug): string {
        return (
            `${drug.getActiveSubstanceTitle()} ${drug.frequencyIfReception || ''} ${drug.directions || ''} ` +
            `${drug.startDate ? DateHelper.format(new Date(drug.startDate), DateFormat.DateWithDotSeparator) : ''} - ${
                drug.endDate ? DateHelper.format(new Date(drug.endDate), DateFormat.DateWithDotSeparator) : ''
            }`
        );
    }

    public static getActiveSubstanceTitle(activeSubstance: string, locale?: Locale): string {
        const dictionary = isDefined(locale)
            ? store.dictionary.getTranslatedDictionary(locale, DictionaryNames.ElementDrug)
            : store.dictionary.getDictionary(DictionaryNames.ElementDrug);

        return dictionary?.get(activeSubstance)?.title || '';
    }

    public static getTradeNameTitle(tradeName: string): string {
        const dictionary = store.dictionary.getDictionary(DictionaryNames.DrugTradeNames);
        return dictionary.get(tradeName)?.title || '';
    }

    public static getTradeNameTitles(tradeNameIds: string[], customTradeNames: string[], locale?: Locale): string {
        const dictionary = isDefined(locale)
            ? store.dictionary.getTranslatedDictionary(locale, DictionaryNames.DrugTradeNames)
            : store.dictionary.getDictionary(DictionaryNames.DrugTradeNames);
        const dictionaryTradeNames = tradeNameIds.map(id => dictionary?.get(id)?.title || '');

        return [...dictionaryTradeNames, ...customTradeNames].join(' / ');
    }

    constructor(params: EntryDrugParams[], name: string) {
        super(params, name);
        makeObservable(this);
    }

    @override
    public get hasData(): boolean {
        return this.fieldValue.serialize().length > 0;
    }

    public get hasHtmlPreview(): boolean {
        return true;
    }

    public getPreview(): string {
        const drugs = this.fieldValue.values;
        return drugs.map(this.getDrugRevision, this).join('\n');
    }

    public getItemRevision(id: string): string {
        const drug = this.fieldValue.getExisting(id);

        return DrugField.getDrugPreview(drug);
    }

    public getItemTitle(id: string): string {
        const drug = this.fieldValue.getExisting(id);

        return `${drug.getTradeNameTitle()}`;
    }

    public makeUpdateParams() {
        return this.fieldValue.serialize();
    }

    public isEqual(value: EntryDrugList): boolean {
        const drugs = this.makeUpdateParams();

        return isEqual(drugs, value.serialize());
    }

    public getDrugRevision(drug: EntryDrug): string {
        return `<strong>${escape(drug.getTradeNameTitle())}</strong> ${escape(DrugField.getDrugPreview(drug))}`;
    }

    public clone(value: EntryDrugParams[] = this.fieldValue.serialize()): DrugField {
        return new DrugField(value, this.fieldName);
    }

    protected setDefaultValueValue(): void {
        this.defaultNullValue = [];
    }

    protected setFieldValue(value: ListValueField<EntryDrugParams>): void {
        const formattedValues = this.makeValueArray(value);

        this.fieldValue = new EntryDrugList(formattedValues);
    }
}
